import { ArrowUpDown, Filter, List as ListIcon, GitCommitHorizontal, Download } from "lucide-react";

const SORTS = [
  { value: "order", label: "Manual order" },
  { value: "priority", label: "Priority" },
  { value: "due_date", label: "Due date" },
  { value: "title", label: "Title (A→Z)" },
  { value: "created_at", label: "Recently added" },
];

const PRIORITY_FILTERS = [
  { value: "all", label: "All priorities" },
  { value: "high", label: "High only" },
  { value: "medium", label: "Medium only" },
  { value: "low", label: "Low only" },
];

const DUE_FILTERS = [
  { value: "all", label: "Any due date" },
  { value: "overdue", label: "Overdue" },
  { value: "today", label: "Due today" },
  { value: "week", label: "Due this week" },
  { value: "none", label: "No due date" },
];

/**
 * ListToolbar — Sort/filter + view toggle + export trigger for the active task list.
 * Hidden on Completed/All views; shows when a specific list is selected.
 */
export default function ListToolbar({
  sort, setSort,
  priorityFilter, setPriorityFilter,
  dueFilter, setDueFilter,
  view, setView,
  onExport,
  canExport,
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4 text-xs" data-testid="list-toolbar">
      <ToolbarSelect
        icon={<ArrowUpDown className="w-3 h-3" strokeWidth={1.75} />}
        value={sort}
        onChange={setSort}
        options={SORTS}
        testid="toolbar-sort"
        label="Sort"
      />
      <ToolbarSelect
        icon={<Filter className="w-3 h-3" strokeWidth={1.75} />}
        value={priorityFilter}
        onChange={setPriorityFilter}
        options={PRIORITY_FILTERS}
        testid="toolbar-filter-priority"
        label="Priority"
      />
      <ToolbarSelect
        icon={<Filter className="w-3 h-3" strokeWidth={1.75} />}
        value={dueFilter}
        onChange={setDueFilter}
        options={DUE_FILTERS}
        testid="toolbar-filter-due"
        label="Due"
      />

      <div className="ml-auto flex items-center gap-1.5">
        <div className="flex border border-zinc-300" role="tablist" aria-label="View" data-testid="toolbar-view">
          <button
            type="button"
            onClick={() => setView("list")}
            className={`px-2.5 h-8 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] ${view === "list" ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-50"}`}
            data-testid="toolbar-view-list"
          >
            <ListIcon className="w-3 h-3" strokeWidth={1.75} /> List
          </button>
          <button
            type="button"
            onClick={() => setView("timeline")}
            className={`px-2.5 h-8 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] border-l border-zinc-300 ${view === "timeline" ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-50"}`}
            data-testid="toolbar-view-timeline"
          >
            <GitCommitHorizontal className="w-3 h-3" strokeWidth={1.75} /> Timeline
          </button>
        </div>
        {canExport && (
          <button
            type="button"
            onClick={onExport}
            className="inline-flex items-center gap-1 h-8 px-2.5 border border-zinc-300 text-[10px] uppercase tracking-[0.1em] text-zinc-700 hover:bg-zinc-50 hover:border-zinc-900 transition-colors"
            data-testid="toolbar-export-btn"
            title="Export this list"
          >
            <Download className="w-3 h-3" strokeWidth={1.75} /> Export
          </button>
        )}
      </div>
    </div>
  );
}

function ToolbarSelect({ icon, value, onChange, options, testid, label }) {
  return (
    <label className="inline-flex items-center gap-1.5 border border-zinc-300 h-8 pl-2 pr-1 hover:border-zinc-900 transition-colors">
      <span className="text-zinc-500">{icon}</span>
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent text-xs h-full pr-1 outline-none"
        data-testid={testid}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
